import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom"; // Import Portal
import { X, CreditCard, Smartphone, Banknote, Loader2, Lock, Calendar, Tag } from "lucide-react";

const PaymentModal = ({ isOpen, onClose, packageData, onPaymentSuccess }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [method, setMethod] = useState("upi");
  const [couponCode, setCouponCode] = useState("");

  // 1. Handle Scroll Lock and Event Blocking
  useEffect(() => {
    if (isOpen) {
      // Block scrolling
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none"; // Blocks touch scrolling on mobile
    } else {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "auto";
    }

    return () => {
      document.body.style.overflow = "unset";
      document.body.style.touchAction = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFinalPayment = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      onPaymentSuccess(method, couponCode);
    }, 2000);
  };

  const formatOrdinalDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    return `${day}${getOrdinal(day)} ${month} ${year}`;
  };

  // 2. Use createPortal to render the modal at the root of the document
  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      // Prevent any wheel or touch events from reaching the background
      onWheel={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <div 
        className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-pink-600 p-6 text-white relative">
          <button onClick={onClose} className="absolute right-4 top-4 hover:bg-white/20 rounded-full p-1 transition-colors">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-bold flex items-center gap-2"><Lock className="w-4 h-4" /> Secure Checkout</h2>
          <p className="text-pink-100 text-xs mt-1">Complete your subscription</p>
        </div>

        <div className="p-6 max-h-[85vh] overflow-y-auto">
          <div className="bg-pink-50 rounded-2xl p-4 mb-4 border border-pink-100">
            <div className="flex justify-between items-center mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-pink-400 font-bold">Total Amount</p>
                <p className="text-2xl font-black text-pink-600">₹{packageData.selling_price}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-gray-800">{packageData.name}</p>
                <p className="text-[10px] text-gray-500">{packageData.padCount}</p>
              </div>
            </div>
            
            <div className="pt-3 border-t border-pink-200 flex items-center gap-2 text-pink-700">
              <Calendar className="w-3.5 h-3.5" />
              <p className="text-[11px] font-bold">
                Valid until: <span className="text-gray-900 font-black">
                  {formatOrdinalDate(packageData.endDate)}
                </span>
              </p>
            </div>
          </div>

          <div className="mb-6">
            <p className="text-[10px] uppercase font-bold text-gray-400 ml-1 mb-2">Have a Coupon?</p>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input 
                type="text" 
                placeholder="Enter Coupon Code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-pink-500 transition-all"
              />
            </div>
          </div>

          <div className="space-y-3 mb-8">
             <p className="text-[10px] uppercase font-bold text-gray-400 ml-1">Payment Method</p>
            {['upi', 'card', 'net'].map((id) => (
              <label key={id} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${method === id ? 'border-pink-500 bg-pink-50' : 'border-gray-100'}`}>
                <input type="radio" className="hidden" checked={method === id} onChange={() => setMethod(id)} />
                <span className={`text-xs font-bold ${method === id ? 'text-pink-700' : 'text-gray-600'}`}>
                  {id === 'upi' ? 'UPI (GPay, PhonePe)' : id === 'card' ? 'Credit / Debit Card' : 'Net Banking'}
                </span>
              </label>
            ))}
          </div>

          <button 
            onClick={handleFinalPayment} 
            disabled={isProcessing} 
            className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold flex justify-center items-center gap-2 disabled:opacity-70"
          >
            {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay ₹${packageData.selling_price} Now`}
          </button>
        </div>
      </div>
    </div>,
    document.body // This is the portal target
  );
};

export default PaymentModal;